import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Container,
  Heading,
  FormErrorMessage,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Select,
} from '@chakra-ui/react';
import { SettingsIcon } from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import UserCredentialsModal from '../UserCredentialsModal';
import LogStream from './LogStream';

function EditClient() {
  const { clientId } = useParams();
  const [client, setClient] = useState({
    name: '',
    email: '',
    databaseName: '',
    cvr: '',
    contactPerson: '',
    phone: '',
    payrollSystem: ''
  });
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [errors, setErrors] = useState({});
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'client',
  });
  const [showCredentials, setShowCredentials] = useState(false);
  const [newUserCredentials, setNewUserCredentials] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const { isOpen: isDeleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const { isOpen: isActivateDialogOpen, onOpen: onActivateDialogOpen, onClose: onActivateDialogClose } = useDisclosure();
  const cancelRef = React.useRef();

  const fetchClientData = useCallback(async () => {
    try {
      const data = await adminApi.getClient(clientId);
      setClient(data);
    } catch (error) {
      toast({
        title: 'Fejl ved hentning af kunde',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [clientId, toast]);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await adminApi.getClientUsers(clientId);
      setUsers(data);
    } catch (error) {
      toast({
        title: 'Fejl ved hentning af brugere',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [clientId, toast]);

  useEffect(() => {
    fetchClientData();
    fetchUsers();
  }, [clientId, fetchClientData, fetchUsers]);

  const validateForm = () => {
    const newErrors = {};
    if (!client.name) newErrors.name = 'Kundenavn er påkrævet';
    if (!client.email) newErrors.email = 'Email er påkrævet';
    if (!client.cvr) newErrors.cvr = 'CVR nummer er påkrævet';
    if (!client.contactPerson) newErrors.contactPerson = 'Kontaktperson er påkrævet';
    if (!client.payrollSystem) newErrors.payrollSystem = 'Lønsystem er påkrævet';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await adminApi.updateClient(clientId, {
        name: client.name,
        email: client.email,
        cvr: client.cvr,
        contactPerson: client.contactPerson,
        phone: client.phone,
        payrollSystem: client.payrollSystem
      });
      toast({
        title: 'Kunde opdateret',
        description: 'Kunden er blevet opdateret succesfuldt',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      navigate('/admin');
    } catch (error) {
      toast({
        title: 'Fejl ved opdatering',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClient(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateUser = async () => {
    setIsCreatingUser(true);
    try {
      const tempPassword = 'DinLon2024!'; // Midlertidigt password
      await adminApi.createClientUser(clientId, { ...newUser, password: tempPassword });
      
      // Gem brugeroplysninger til visning i modal
      setNewUserCredentials({
        email: newUser.email,
        tempPassword: tempPassword
      });
      setShowCredentials(true);
      
      onClose();
      fetchUsers();
      setNewUser({
        name: '',
        email: '',
        role: 'client',
      });

      toast({
        title: 'Bruger oprettet',
        description: 'Brugeren er blevet oprettet succesfuldt',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Fejl ved oprettelse',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setSelectedUserId(userId);
    onDeleteDialogOpen();
  };

  const confirmDeleteUser = async () => {
    try {
      await adminApi.deleteClientUser(clientId, selectedUserId);
      toast({
        title: 'Bruger deaktiveret',
        description: 'Brugeren er blevet deaktiveret succesfuldt',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Fejl ved deaktivering',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      onDeleteDialogClose();
      setSelectedUserId(null);
    }
  };

  const handleActivateUser = async (userId) => {
    setSelectedUserId(userId);
    onActivateDialogOpen();
  };

  const confirmActivateUser = async () => {
    try {
      await adminApi.activateClientUser(clientId, selectedUserId);
      toast({
        title: 'Bruger aktiveret',
        description: 'Brugeren er blevet aktiveret succesfuldt',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Fejl ved aktivering',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      onActivateDialogClose();
      setSelectedUserId(null);
    }
  };

  return (
    <Container maxW="container.xl" py={5}>
      <VStack spacing={5} align="stretch">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Heading size="lg">Rediger kunde: {client.name}</Heading>
          <IconButton
            icon={<SettingsIcon />}
            onClick={() => setShowLogs(!showLogs)}
            variant="ghost"
            colorScheme="gray"
            aria-label="Vis logs"
          />
        </Box>
        
        <Tabs>
          <TabList>
            <Tab>Kunde information</Tab>
            <Tab>Brugere</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <Box as="form" onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <FormControl isInvalid={errors.name}>
                    <FormLabel>Kundenavn</FormLabel>
                    <Input
                      name="name"
                      value={client.name}
                      onChange={handleChange}
                      placeholder="Indtast kundenavn"
                    />
                    <FormErrorMessage>{errors.name}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.cvr}>
                    <FormLabel>CVR Nummer</FormLabel>
                    <Input
                      name="cvr"
                      value={client.cvr}
                      onChange={handleChange}
                      placeholder="Indtast CVR nummer"
                    />
                    <FormErrorMessage>{errors.cvr}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.contactPerson}>
                    <FormLabel>Kontaktperson</FormLabel>
                    <Input
                      name="contactPerson"
                      value={client.contactPerson}
                      onChange={handleChange}
                      placeholder="Indtast kontaktperson"
                    />
                    <FormErrorMessage>{errors.contactPerson}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.email}>
                    <FormLabel>E-mail</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={client.email}
                      onChange={handleChange}
                      placeholder="kunde@eksempel.dk"
                    />
                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Telefon</FormLabel>
                    <Input
                      name="phone"
                      value={client.phone}
                      onChange={handleChange}
                      placeholder="Indtast telefonnummer"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Database Navn</FormLabel>
                    <Input
                      name="databaseName"
                      value={client.databaseName}
                      isReadOnly
                      bg="gray.100"
                    />
                  </FormControl>

                  <FormControl isInvalid={errors.payrollSystem}>
                    <FormLabel>Lønsystem</FormLabel>
                    <Select
                      name="payrollSystem"
                      value={client.payrollSystem}
                      onChange={handleChange}
                      placeholder="Vælg lønsystem"
                    >
                      <option value="Lessor">Lessor</option>
                      <option value="Zenegy">Zenegy</option>
                    </Select>
                    <FormErrorMessage>{errors.payrollSystem}</FormErrorMessage>
                  </FormControl>

                  <Button
                    colorScheme="blue"
                    type="submit"
                    isLoading={isLoading}
                    width="full"
                  >
                    Gem ændringer
                  </Button>
                </VStack>
              </Box>
            </TabPanel>

            <TabPanel>
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Heading size="md">Brugere</Heading>
                  <Button colorScheme="blue" onClick={onOpen}>
                    Opret ny bruger
                  </Button>
                </HStack>

                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Navn</Th>
                      <Th>Email</Th>
                      <Th>Rolle</Th>
                      <Th>Status</Th>
                      <Th>Handlinger</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {users.map((user) => (
                      <Tr key={user.id} opacity={user.active ? 1 : 0.6}>
                        <Td>{user.name}</Td>
                        <Td>{user.email}</Td>
                        <Td>{user.role}</Td>
                        <Td>{user.active ? 'Aktiv' : 'Deaktiveret'}</Td>
                        <Td>
                          <HStack spacing={2}>
                            {user.active ? (
                              <Button
                                size="sm"
                                colorScheme="red"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                Deaktiver
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                colorScheme="green"
                                onClick={() => handleActivateUser(user.id)}
                              >
                                Aktivér
                              </Button>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Opret ny bruger</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Navn</FormLabel>
                  <Input
                    name="name"
                    value={newUser.name}
                    onChange={handleNewUserChange}
                    placeholder="Indtast navn"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={newUser.email}
                    onChange={handleNewUserChange}
                    placeholder="bruger@eksempel.dk"
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Annuller
              </Button>
              <Button 
                colorScheme="blue" 
                onClick={handleCreateUser}
                isLoading={isCreatingUser}
                loadingText="Opretter bruger..."
              >
                Opret bruger
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <AlertDialog
          isOpen={isDeleteDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={onDeleteDialogClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Deaktiver bruger
              </AlertDialogHeader>

              <AlertDialogBody>
                Er du sikker på at du vil deaktivere denne bruger? Denne handling kan ikke fortrydes.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeleteDialogClose}>
                  Annuller
                </Button>
                <Button colorScheme="red" onClick={confirmDeleteUser} ml={3}>
                  Deaktiver
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        <AlertDialog
          isOpen={isActivateDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={onActivateDialogClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Aktivér bruger
              </AlertDialogHeader>

              <AlertDialogBody>
                Er du sikker på at du vil aktivere denne bruger? Brugeren vil igen få adgang til systemet.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onActivateDialogClose}>
                  Annuller
                </Button>
                <Button colorScheme="green" onClick={confirmActivateUser} ml={3}>
                  Aktivér
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </VStack>

      {showCredentials && newUserCredentials && (
        <UserCredentialsModal 
          user={newUserCredentials}
          onClose={() => setShowCredentials(false)}
        />
      )}

      <LogStream 
        isOpen={showLogs}
        onClose={() => setShowLogs(false)}
      />
    </Container>
  );
}

export default EditClient; 